/**
 * User & Access Management Component
 * All layout-critical styles are INLINE to prevent parent CSS overrides
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config';
import '../../styles/UserManagement.css';

// ===== AUTH =====
const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return { headers: { 'Authorization': `Bearer ${token}` }, withCredentials: true };
};

// ===== INLINE STYLE CONSTANTS =====
const S = {
  // Grid: 7-column permission table
  permHeader: { display: 'grid', gridTemplateColumns: '2fr repeat(6, 1fr)', padding: '12px 16px', background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)', color: 'white', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', alignItems: 'center', minHeight: '50px' },
  permRow: { display: 'grid', gridTemplateColumns: '2fr repeat(6, 1fr)', padding: '10px 16px', borderBottom: '1px solid #f1f5f9', alignItems: 'center', fontSize: '13px', background: 'white', minHeight: '44px' },
  permRowHidden: { display: 'grid', gridTemplateColumns: '2fr repeat(6, 1fr)', padding: '10px 16px', borderBottom: '1px solid #f1f5f9', alignItems: 'center', fontSize: '13px', background: '#fef2f2', minHeight: '44px' },
  // Grid: stats
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' },
  statCard: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '16px', background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0' },
  statInfo: { display: 'flex', flexDirection: 'column' },
  // Grid: form rows
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' },
  // Flex helpers
  flexRow: { display: 'flex', flexDirection: 'row', alignItems: 'center' },
  flexCol: { display: 'flex', flexDirection: 'column' },
  // Column cells
  cellCenter: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cellLeft: { display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '20px', gap: '8px', color: '#374151' },
  cellLeftHidden: { display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '20px', gap: '8px', color: '#94a3b8', textDecoration: 'line-through' },
  // Header column
  headerCenter: { textAlign: 'center' },
  headerLeft: { textAlign: 'left' },
  checkCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' },
  hideColHeader: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', background: 'rgba(239, 68, 68, 0.3)', borderRadius: '4px', padding: '4px 8px' },
  // Category row
  category: { display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)', color: 'white', padding: '10px 16px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' },
  // Quick actions
  quickActions: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' },
  // Header
  header: { display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  // Tabs
  tabs: { display: 'flex', flexDirection: 'row', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #e2e8f0' },
  // Filters
  filters: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '12px', alignItems: 'center', background: 'white', padding: '16px 20px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #e2e8f0' },
  // Modal
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 10000, padding: '30px', overflowY: 'auto', backdropFilter: 'blur(4px)' },
  modal: { background: 'white', borderRadius: '16px', width: '100%', maxWidth: '950px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', margin: 'auto 0', display: 'flex', flexDirection: 'column', maxHeight: '90vh' },
  modalSm: { background: 'white', borderRadius: '16px', width: '100%', maxWidth: '450px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', margin: 'auto 0', display: 'flex', flexDirection: 'column', maxHeight: '90vh' },
  modalHeader: { display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)', color: 'white', padding: '18px 24px', borderRadius: '16px 16px 0 0', flexShrink: 0 },
  modalBody: { padding: '24px', overflowY: 'auto', flex: 1, minHeight: 0 },
  modalFooter: { display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: '0 0 16px 16px', flexShrink: 0 },
};

// ===== HELPERS =====
const getInitials = (name) => {
  if (!name) return '?';
  const words = name.trim().split(' ');
  return words.length === 1 ? words[0].charAt(0).toUpperCase() : (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

const avatarColors = ['green', 'blue', 'purple', 'orange', 'cyan', 'red'];
const getAvatarColor = (id) => avatarColors[(id || 0) % avatarColors.length];

// Inline badge styles (parent CSS overrides class-based badges)
const badgeBase = { padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, display: 'inline-block', whiteSpace: 'nowrap' };
const roleBadgeStyles = {
  super_admin: { ...badgeBase, background: '#fef2f2', color: '#dc2626' },
  admin: { ...badgeBase, background: '#fefce8', color: '#ca8a04' },
  manager: { ...badgeBase, background: '#e3f2fd', color: '#1565c0' },
  incharge: { ...badgeBase, background: '#f5f3ff', color: '#7c3aed' },
  supervisor: { ...badgeBase, background: '#ecfeff', color: '#0891b2' },
  operator: { ...badgeBase, background: '#f0fdf4', color: '#16a34a' },
  viewer: { ...badgeBase, background: '#f1f5f9', color: '#64748b' },
  factory_head: { ...badgeBase, background: '#fff7ed', color: '#ea580c' },
  default: { ...badgeBase, background: '#f1f5f9', color: '#64748b' },
};
const getRoleBadgeStyle = (code) => roleBadgeStyles[code] || roleBadgeStyles.default;

const statusBadgeStyles = {
  active: { ...badgeBase, background: '#f0fdf4', color: '#16a34a', cursor: 'pointer' },
  inactive: { ...badgeBase, background: '#fef2f2', color: '#dc2626', cursor: 'pointer' },
};

const countBadgeStyle = { ...badgeBase, background: '#e3f2fd', color: '#1565c0' };
const countBadgeGreenStyle = { ...badgeBase, background: '#f0fdf4', color: '#16a34a' };

const roleIcons = {
  super_admin: '👑', admin: '⚙️', manager: '💼',
  incharge: '📋', supervisor: '🔰', operator: '🔧', viewer: '👁️',
};

// ===== PERMISSION TABLE COMPONENT =====
const PermissionsTable = ({ screens, permissions, onChange, showExpandCollapse }) => {
  const [collapsed, setCollapsed] = useState({});

  const grouped = {};
  (screens || []).forEach(s => {
    if (!grouped[s.category]) grouped[s.category] = [];
    grouped[s.category].push(s);
  });
  const categories = Object.keys(grouped).sort((a, b) => {
    const ao = grouped[a][0]?.category_order || 0;
    const bo = grouped[b][0]?.category_order || 0;
    return ao - bo;
  });

  const getPerm = (screenId) => permissions[screenId] || { is_hidden: 0, can_view: 0, can_create: 0, can_edit: 0, can_delete: 0, can_export: 0 };

  const handleCheck = (screenId, field, value) => {
    const current = { ...getPerm(screenId) };
    current[field] = value ? 1 : 0;
    if (field === 'is_hidden' && value) {
      current.can_view = 0; current.can_create = 0; current.can_edit = 0; current.can_delete = 0; current.can_export = 0;
    }
    onChange({ ...permissions, [screenId]: current });
  };

  const toggleColumnAll = (field, value) => {
    const updated = { ...permissions };
    (screens || []).forEach(s => {
      const p = { ...(updated[s.id] || getPerm(s.id)) };
      if (field === 'is_hidden') {
        p.is_hidden = value ? 1 : 0;
        if (value) { p.can_view = 0; p.can_create = 0; p.can_edit = 0; p.can_delete = 0; p.can_export = 0; }
      } else {
        if (!p.is_hidden) {
          if (field === 'can_view' && s.has_view != 0) p.can_view = value ? 1 : 0;
          if (field === 'can_create' && s.has_create != 0) p.can_create = value ? 1 : 0;
          if (field === 'can_edit' && s.has_edit != 0) p.can_edit = value ? 1 : 0;
          if (field === 'can_delete' && s.has_delete != 0) p.can_delete = value ? 1 : 0;
          if (field === 'can_export' && s.has_export != 0) p.can_export = value ? 1 : 0;
        }
      }
      updated[s.id] = p;
    });
    onChange(updated);
  };

  const toggleCategoryAll = (cat) => {
    const catScreens = grouped[cat] || [];
    const allChecked = catScreens.every(s => getPerm(s.id).can_view);
    const updated = { ...permissions };
    catScreens.forEach(s => {
      const p = { ...(updated[s.id] || getPerm(s.id)) };
      if (allChecked) {
        p.is_hidden = 0; p.can_view = 0; p.can_create = 0; p.can_edit = 0; p.can_delete = 0; p.can_export = 0;
      } else {
        p.is_hidden = 0; p.can_view = 1;
        if (s.has_create != 0) p.can_create = 1;
        if (s.has_edit != 0) p.can_edit = 1;
        if (s.has_delete != 0) p.can_delete = 1;
        if (s.has_export != 0) p.can_export = 1;
      }
      updated[s.id] = p;
    });
    onChange(updated);
  };

  const quickAction = (type) => {
    const updated = { ...permissions };
    (screens || []).forEach(s => {
      const p = { is_hidden: 0, can_view: 0, can_create: 0, can_edit: 0, can_delete: 0, can_export: 0 };
      if (type === 'select_all') {
        p.can_view = s.has_view != 0 ? 1 : 0; p.can_create = s.has_create != 0 ? 1 : 0;
        p.can_edit = s.has_edit != 0 ? 1 : 0; p.can_delete = s.has_delete != 0 ? 1 : 0;
        p.can_export = s.has_export != 0 ? 1 : 0;
      } else if (type === 'view_only') {
        p.can_view = s.has_view != 0 ? 1 : 0;
      } else if (type === 'view_export') {
        p.can_view = s.has_view != 0 ? 1 : 0; p.can_export = s.has_export != 0 ? 1 : 0;
      }
      updated[s.id] = p;
    });
    onChange(updated);
  };

  const allScreens = screens || [];
  const isColChecked = (field) => allScreens.length > 0 && allScreens.every(s => {
    const p = getPerm(s.id);
    if (field === 'is_hidden') return p.is_hidden;
    if (p.is_hidden) return true;
    const hasField = s[`has_${field.replace('can_', '')}`];
    return hasField == 0 || p[field];
  });

  const chkStyle = { width: 18, height: 18, accentColor: '#1565c0', cursor: 'pointer', margin: 0 };
  const hideChkStyle = { ...chkStyle, accentColor: '#ef4444' };
  const headerChkStyle = { width: 16, height: 16, accentColor: '#22c55e', cursor: 'pointer', margin: 0 };

  return (
    <>
      {/* Quick Actions */}
      <div style={S.quickActions}>
        <button className="um-quick-btn select-all" onClick={() => quickAction('select_all')}>✓ Select All</button>
        <button className="um-quick-btn deselect-all" onClick={() => quickAction('deselect_all')}>✕ Deselect All</button>
        <button className="um-quick-btn view-only" onClick={() => quickAction('view_only')}>👁️ View Only</button>
        <button className="um-quick-btn view-only" onClick={() => quickAction('view_export')}>📤 View + Export</button>
        {showExpandCollapse && (
          <>
            <button className="um-quick-btn expand" onClick={() => setCollapsed({})}>📂 Expand All</button>
            <button className="um-quick-btn expand" onClick={() => {
              const c = {}; categories.forEach(cat => c[cat] = true); setCollapsed(c);
            }}>📁 Collapse All</button>
          </>
        )}
      </div>

      {/* Permissions Table */}
      <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', width: '100%' }}>
        {/* HEADER ROW - 7 columns */}
        <div style={S.permHeader}>
          <div style={S.headerLeft}>MODULE</div>
          <div style={S.hideColHeader}>
            HIDE<br />
            <input type="checkbox" style={headerChkStyle} checked={isColChecked('is_hidden')} onChange={e => toggleColumnAll('is_hidden', e.target.checked)} />
          </div>
          <div style={S.checkCol}>
            VIEW<br />
            <input type="checkbox" style={headerChkStyle} checked={isColChecked('can_view')} onChange={e => toggleColumnAll('can_view', e.target.checked)} />
          </div>
          <div style={S.checkCol}>
            CREATE<br />
            <input type="checkbox" style={headerChkStyle} checked={isColChecked('can_create')} onChange={e => toggleColumnAll('can_create', e.target.checked)} />
          </div>
          <div style={S.checkCol}>
            EDIT<br />
            <input type="checkbox" style={headerChkStyle} checked={isColChecked('can_edit')} onChange={e => toggleColumnAll('can_edit', e.target.checked)} />
          </div>
          <div style={S.checkCol}>
            DELETE<br />
            <input type="checkbox" style={headerChkStyle} checked={isColChecked('can_delete')} onChange={e => toggleColumnAll('can_delete', e.target.checked)} />
          </div>
          <div style={S.checkCol}>
            EXPORT<br />
            <input type="checkbox" style={headerChkStyle} checked={isColChecked('can_export')} onChange={e => toggleColumnAll('can_export', e.target.checked)} />
          </div>
        </div>

        {/* Category + Permission Rows */}
        {categories.map(cat => {
          const catScreens = grouped[cat];
          const isCollapsed = collapsed[cat];
          return (
            <React.Fragment key={cat}>
              <div style={S.category} onClick={() => setCollapsed({ ...collapsed, [cat]: !isCollapsed })}>
                <span>{isCollapsed ? '▶' : '▼'} {cat} ({catScreens.length} module{catScreens.length !== 1 ? 's' : ''})</span>
                <button style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', padding: '4px 12px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}
                  onClick={e => { e.stopPropagation(); toggleCategoryAll(cat); }}>Toggle All</button>
              </div>
              {!isCollapsed && catScreens.map(s => {
                const p = getPerm(s.id);
                const isHidden = !!p.is_hidden;
                const rowStyle = isHidden ? S.permRowHidden : S.permRow;
                const nameStyle = isHidden ? S.cellLeftHidden : S.cellLeft;
                return (
                  <div key={s.id} style={rowStyle}>
                    <div style={nameStyle}>{s.icon} {s.screen_name}</div>
                    <div style={S.cellCenter}>
                      <input type="checkbox" style={hideChkStyle} checked={isHidden} onChange={e => handleCheck(s.id, 'is_hidden', e.target.checked)} />
                    </div>
                    {s.has_view != 0
                      ? <div style={S.cellCenter}><input type="checkbox" style={chkStyle} checked={!!p.can_view} disabled={isHidden} onChange={e => handleCheck(s.id, 'can_view', e.target.checked)} /></div>
                      : <div style={{ ...S.cellCenter, color: '#cbd5e1', fontSize: '16px' }}>—</div>}
                    {s.has_create != 0
                      ? <div style={S.cellCenter}><input type="checkbox" style={chkStyle} checked={!!p.can_create} disabled={isHidden} onChange={e => handleCheck(s.id, 'can_create', e.target.checked)} /></div>
                      : <div style={{ ...S.cellCenter, color: '#cbd5e1', fontSize: '16px' }}>—</div>}
                    {s.has_edit != 0
                      ? <div style={S.cellCenter}><input type="checkbox" style={chkStyle} checked={!!p.can_edit} disabled={isHidden} onChange={e => handleCheck(s.id, 'can_edit', e.target.checked)} /></div>
                      : <div style={{ ...S.cellCenter, color: '#cbd5e1', fontSize: '16px' }}>—</div>}
                    {s.has_delete != 0
                      ? <div style={S.cellCenter}><input type="checkbox" style={chkStyle} checked={!!p.can_delete} disabled={isHidden} onChange={e => handleCheck(s.id, 'can_delete', e.target.checked)} /></div>
                      : <div style={{ ...S.cellCenter, color: '#cbd5e1', fontSize: '16px' }}>—</div>}
                    {s.has_export != 0
                      ? <div style={S.cellCenter}><input type="checkbox" style={chkStyle} checked={!!p.can_export} disabled={isHidden} onChange={e => handleCheck(s.id, 'can_export', e.target.checked)} /></div>
                      : <div style={{ ...S.cellCenter, color: '#cbd5e1', fontSize: '16px' }}>—</div>}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
};

// ===== MAIN COMPONENT =====
const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [screens, setScreens] = useState([]);
  const [stats, setStats] = useState({ total_users: 0, active_users: 0, total_roles: 0, total_screens: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [toast, setToast] = useState(null);
  const [userModal, setUserModal] = useState(null);
  const [roleModal, setRoleModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [passwordModal, setPasswordModal] = useState(null);
  const [userForm, setUserForm] = useState({});
  const [userPermissions, setUserPermissions] = useState({});
  const [roleForm, setRoleForm] = useState({});
  const [rolePermissions, setRolePermissions] = useState({});
  const [newPassword, setNewPassword] = useState('');
  const [modules, setModules] = useState([]);
  const [userModules, setUserModules] = useState([]);
  const [defaultModuleId, setDefaultModuleId] = useState(null);
  const [permTab, setPermTab] = useState('COLDSTORE');

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // ===== LOAD DATA =====
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API_ENDPOINTS.USER_ACCESS_MGMT}?type=users`, getAuthConfig()),
        axios.get(`${API_ENDPOINTS.USER_ACCESS_MGMT}?type=stats`, getAuthConfig()),
      ]);
      if (usersRes.data.success) {
        setUsers(usersRes.data.users || []);
        setRoles(usersRes.data.roles || []);
        setScreens(usersRes.data.screens || []);
        setModules(usersRes.data.modules || []);
      }
      if (statsRes.data.success) setStats(statsRes.data.stats);
    } catch (err) {
      console.error('Load error:', err);
      showToast('Failed to load data', 'error');
    } finally { setLoading(false); }
  }, [showToast]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadRoles = useCallback(async () => {
    try {
      const res = await axios.get(`${API_ENDPOINTS.USER_ACCESS_MGMT}?type=roles`, getAuthConfig());
      if (res.data.success) setRoles(res.data.roles || []);
    } catch (err) { console.error(err); }
  }, []);

  // ===== USER ACTIONS =====
  const openAddUser = () => {
    setUserForm({ full_name: '', email: '', username: '', password: '', confirm_password: '', employee_id: '', phone: '', designation: '', department: '', um_role_id: '', is_active: true });
    setUserPermissions({});
    setUserModules(modules.map(m => parseInt(m.id)));
    setDefaultModuleId(modules.length > 0 ? parseInt(modules[0].id) : null);
    setPermTab('COLDSTORE');
    setUserModal('add');
  };

  const openEditUser = async (user) => {
    try {
      const res = await axios.get(`${API_ENDPOINTS.USER_ACCESS_MGMT}?type=users&id=${user.id}`, getAuthConfig());
      if (res.data.success) {
        const u = res.data.user;
        setUserForm({
          id: u.id, full_name: u.full_name || '', email: u.email || '', username: u.username || '',
          password: '', confirm_password: '', employee_id: u.employee_id || '', phone: u.phone || '',
          designation: u.designation || '', department: u.department || '',
          um_role_id: u.um_role_id || '', is_active: u.is_active,
        });
        const perms = {};
        (u.permissions || []).forEach(p => {
          perms[p.screen_id] = {
            is_hidden: parseInt(p.is_hidden), can_view: parseInt(p.can_view),
            can_create: parseInt(p.can_create), can_edit: parseInt(p.can_edit),
            can_delete: parseInt(p.can_delete), can_export: parseInt(p.can_export),
          };
        });
        setUserPermissions(perms);
        setUserModules(u.module_ids || []);
        setDefaultModuleId(u.default_module_id || (u.module_ids && u.module_ids.length > 0 ? u.module_ids[0] : null));
        setPermTab('COLDSTORE');
        setUserModal('edit');
      }
    } catch (err) { showToast('Failed to load user details', 'error'); }
  };

  const saveUser = async () => {
    if (!userForm.full_name || !userForm.email) { showToast('Full Name and Email are required', 'error'); return; }
    if (userModal === 'add' && !userForm.password) { showToast('Password is required', 'error'); return; }
    if (userForm.password && userForm.password !== userForm.confirm_password) { showToast('Passwords do not match', 'error'); return; }
    if (userModules.length === 0) { showToast('Please select at least one module', 'error'); return; }
    setSaving(true);
    try {
      const permArray = Object.entries(userPermissions).map(([sid, p]) => ({ screen_id: parseInt(sid), ...p }));
      const payload = { ...userForm, permissions: permArray, role: '', module_ids: userModules, default_module_id: defaultModuleId };
      if (userForm.um_role_id) {
        const r = roles.find(r => r.id == userForm.um_role_id);
        if (r) payload.role = r.role_code;
      }
      if (userModal === 'edit') {
        await axios.put(`${API_ENDPOINTS.USER_ACCESS_MGMT}?type=user`, payload, getAuthConfig());
        await axios.post(`${API_ENDPOINTS.USER_ACCESS_MGMT}?action=save_user_permissions`, { user_id: userForm.id, permissions: permArray }, getAuthConfig());
        await axios.post(`${API_ENDPOINTS.USER_ACCESS_MGMT}?action=save_user_modules`, { user_id: userForm.id, module_ids: userModules, default_module_id: defaultModuleId }, getAuthConfig());
        showToast('User updated successfully');
      } else {
        if (!userForm.username) payload.username = userForm.email.split('@')[0];
        const createRes = await axios.post(`${API_ENDPOINTS.USER_ACCESS_MGMT}?action=create_user`, payload, getAuthConfig());
        // After create, also save permissions and modules separately (like edit) to ensure they persist
        if (createRes.data.success && createRes.data.user_id) {
          const newUserId = createRes.data.user_id;
          await axios.post(`${API_ENDPOINTS.USER_ACCESS_MGMT}?action=save_user_permissions`, { user_id: newUserId, permissions: permArray }, getAuthConfig());
          await axios.post(`${API_ENDPOINTS.USER_ACCESS_MGMT}?action=save_user_modules`, { user_id: newUserId, module_ids: userModules, default_module_id: defaultModuleId }, getAuthConfig());
        }
        showToast('User created successfully');
      }
      setUserModal(null);
      loadData();
    } catch (err) { showToast(err.response?.data?.message || 'Failed to save user', 'error'); }
    finally { setSaving(false); }
  };

  const toggleUserStatus = async (user) => {
    try {
      await axios.post(`${API_ENDPOINTS.USER_ACCESS_MGMT}?action=toggle_status`, { user_id: user.id }, getAuthConfig());
      showToast(`User ${user.is_active ? 'deactivated' : 'activated'}`);
      loadData();
    } catch (err) { showToast('Failed to toggle status', 'error'); }
  };

  const deleteUser = async () => {
    if (!deleteModal) return;
    try {
      await axios.delete(`${API_ENDPOINTS.USER_ACCESS_MGMT}?type=user&id=${deleteModal.id}`, getAuthConfig());
      showToast('User deleted');
      setDeleteModal(null);
      loadData();
    } catch (err) { showToast('Failed to delete', 'error'); }
  };

  const resetPassword = async () => {
    if (!newPassword || !passwordModal) return;
    try {
      await axios.post(`${API_ENDPOINTS.USER_ACCESS_MGMT}?action=reset_password`, { user_id: passwordModal.id, new_password: newPassword }, getAuthConfig());
      showToast('Password reset successfully');
      setPasswordModal(null);
      setNewPassword('');
    } catch (err) { showToast('Failed to reset password', 'error'); }
  };

  const handleRoleChange = async (roleId) => {
    setUserForm(prev => ({ ...prev, um_role_id: roleId }));
    if (roleId) {
      try {
        const res = await axios.get(`${API_ENDPOINTS.USER_ACCESS_MGMT}?type=role_permissions&role_id=${roleId}`, getAuthConfig());
        if (res.data.success) {
          const perms = {};
          Object.values(res.data.permissions).forEach(p => {
            perms[p.screen_id] = {
              is_hidden: parseInt(p.is_hidden), can_view: parseInt(p.can_view),
              can_create: parseInt(p.can_create), can_edit: parseInt(p.can_edit),
              can_delete: parseInt(p.can_delete), can_export: parseInt(p.can_export),
            };
          });
          setUserPermissions(perms);
        }
      } catch (err) { console.error(err); }
    }
  };

  // ===== ROLE ACTIONS =====
  const openAddRole = () => {
    setRoleForm({ role_name: '', description: '' });
    const perms = {};
    screens.forEach(s => {
      perms[s.id] = { is_hidden: 0, can_view: s.has_view != 0 ? 1 : 0, can_create: 0, can_edit: 0, can_delete: 0, can_export: s.has_export != 0 ? 1 : 0 };
    });
    setRolePermissions(perms);
    setPermTab('COLDSTORE');
    setRoleModal('add');
  };

  const openEditRole = async (role) => {
    try {
      const res = await axios.get(`${API_ENDPOINTS.USER_ACCESS_MGMT}?type=roles&id=${role.id}`, getAuthConfig());
      if (res.data.success) {
        const r = res.data.role;
        setRoleForm({ id: r.id, role_name: r.role_name, description: r.description || '', is_system: r.is_system });
        const perms = {};
        (r.permissions || []).forEach(p => {
          perms[p.screen_id] = {
            is_hidden: parseInt(p.is_hidden), can_view: parseInt(p.can_view),
            can_create: parseInt(p.can_create), can_edit: parseInt(p.can_edit),
            can_delete: parseInt(p.can_delete), can_export: parseInt(p.can_export),
          };
        });
        setRolePermissions(perms);
        setPermTab('COLDSTORE');
        setRoleModal('edit');
      }
    } catch (err) { showToast('Failed to load role details', 'error'); }
  };

  const saveRole = async () => {
    if (!roleForm.role_name) { showToast('Role name is required', 'error'); return; }
    setSaving(true);
    try {
      const permArray = Object.entries(rolePermissions).map(([sid, p]) => ({ screen_id: parseInt(sid), ...p }));
      if (roleModal === 'edit') {
        await axios.put(`${API_ENDPOINTS.USER_ACCESS_MGMT}?type=role`, roleForm, getAuthConfig());
        await axios.post(`${API_ENDPOINTS.USER_ACCESS_MGMT}?action=save_role_permissions`, { role_id: roleForm.id, permissions: permArray }, getAuthConfig());
        showToast('Role updated & permissions synced');
      } else {
        const res = await axios.post(`${API_ENDPOINTS.USER_ACCESS_MGMT}?action=create_role`, roleForm, getAuthConfig());
        if (res.data.role_id) {
          await axios.post(`${API_ENDPOINTS.USER_ACCESS_MGMT}?action=save_role_permissions`, { role_id: res.data.role_id, permissions: permArray }, getAuthConfig());
        }
        showToast('Role created successfully');
      }
      setRoleModal(null);
      loadData();
      loadRoles();
    } catch (err) { showToast(err.response?.data?.message || 'Failed to save role', 'error'); }
    finally { setSaving(false); }
  };

  const deleteRole = async () => {
    if (!deleteModal) return;
    try {
      await axios.delete(`${API_ENDPOINTS.USER_ACCESS_MGMT}?type=role&id=${deleteModal.id}`, getAuthConfig());
      showToast('Role deleted');
      setDeleteModal(null);
      loadData();
      loadRoles();
    } catch (err) { showToast(err.response?.data?.message || 'Failed to delete role', 'error'); }
  };

  // ===== FILTERS =====
  const filteredUsers = users.filter(u => {
    if (search) {
      const s = search.toLowerCase();
      if (!(u.full_name || '').toLowerCase().includes(s) && !(u.email || '').toLowerCase().includes(s) && !(u.employee_id || '').toLowerCase().includes(s) && !(u.username || '').toLowerCase().includes(s)) return false;
    }
    if (filterRole && u.um_role_id != filterRole) return false;
    if (filterStatus === 'active' && !u.is_active) return false;
    if (filterStatus === 'inactive' && u.is_active) return false;
    return true;
  });

  // ===== RENDER =====
  if (loading) {
    return (
      <div className="um-container">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', gap: '16px', color: '#64748b' }}>
          <div className="um-spinner"></div>
          <span>Loading User Management...</span>
        </div>
      </div>
    );
  }

  // Input style for forms (inline to override everything)
  const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', color: '#1e293b', background: 'white', display: 'block', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' };

  return (
    <div className="um-container">
      {/* Toast */}
      {toast && (
        <div className={`um-toast um-toast-${toast.type}`}>
          {toast.type === 'success' ? '✅' : toast.type === 'error' ? '❌' : '⚠️'} {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={S.header}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>👥 User Management</h1>
          <p style={{ color: '#64748b', fontSize: '14px', margin: '4px 0 0 0' }}>Manage users, roles and access permissions</p>
        </div>
        <button className="um-btn um-btn-primary" onClick={activeTab === 'users' ? openAddUser : openAddRole}>
          ➕ {activeTab === 'users' ? 'Add User' : 'Add Role'}
        </button>
      </div>

      {/* Stats */}
      <div style={S.statsGrid}>
        {[
          { icon: '👥', color: 'blue', bg: '#dbeafe', value: stats.total_users, label: 'Total Users' },
          { icon: '✅', color: 'green', bg: '#dcfce7', value: stats.active_users, label: 'Active Users' },
          { icon: '🔐', color: 'purple', bg: '#ede9fe', value: stats.total_roles, label: 'Roles' },
          { icon: '📦', color: 'orange', bg: '#ffedd5', value: stats.total_screens, label: 'Modules' },
        ].map((s, i) => (
          <div key={i} style={S.statCard}>
            <div style={{ width: 48, minWidth: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, background: s.bg, flexShrink: 0 }}>
              {s.icon}
            </div>
            <div style={S.statInfo}>
              <span style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>{s.value}</span>
              <span style={{ fontSize: '13px', color: '#64748b' }}>{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        <button className={`um-tab${activeTab === 'users' ? ' active' : ''}`} onClick={() => setActiveTab('users')}
          style={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
          👥 Users <span className="um-tab-badge">{users.length}</span>
        </button>
        <button className={`um-tab${activeTab === 'roles' ? ' active' : ''}`}
          onClick={() => { setActiveTab('roles'); loadRoles(); }}
          style={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
          🔐 Roles <span className="um-tab-badge">{roles.length}</span>
        </button>
      </div>

      {/* USERS TAB */}
      {activeTab === 'users' && (
        <>
          <div style={S.filters}>
            <div style={{ flex: 1, minWidth: 200, position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span style={{ position: 'absolute', left: 12, fontSize: 14, color: '#94a3b8', pointerEvents: 'none' }}>🔍</span>
              <input type="text" placeholder="Search by Name, Email, Employee ID..."
                style={{ ...inputStyle, paddingLeft: '36px' }}
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="um-select" value={filterRole} onChange={e => setFilterRole(e.target.value)}>
              <option value="">All Roles</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.role_name}</option>)}
            </select>
            <select className="um-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <button className="um-btn um-btn-secondary um-btn-sm" onClick={() => { setSearch(''); setFilterRole(''); setFilterStatus(''); }}>✕ Clear</button>
          </div>

          <div className="um-table-container">
            <table className="um-table">
              <thead>
                <tr>
                  <th style={{ width: 55 }}>S.No</th>
                  <th>User Name</th>
                  <th>Display Name</th>
                  <th>Email Address</th>
                  <th>Role</th>
                  <th>Modules</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ width: 70, textAlign: 'center' }}>Edit</th>
                  <th style={{ width: 110, textAlign: 'center' }}>Password Reset</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u, i) => (
                  <tr key={u.id}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 500, color: '#1e293b' }}>{u.username || '—'}</td>
                    <td>
                      <div style={{ fontWeight: 600, color: '#1e293b' }}>{u.full_name || '—'}</div>
                    </td>
                    <td style={{ color: '#475569' }}>{u.email || '—'}</td>
                    <td>
                      <span style={getRoleBadgeStyle(u.role_code || u.role)}>
                        {u.role_name || u.role || 'Viewer'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(u.modules && u.modules.length > 0) ? u.modules.map(m => (
                          <span key={m.id} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                            background: (m.color || '#1565c0') + '18', color: m.color || '#1565c0',
                            border: `1px solid ${(m.color || '#1565c0')}30`
                          }}>
                            {m.icon || '📦'} {m.module_name}
                            {m.is_default === '1' || m.is_default === 1 ? <span style={{ fontSize: 9, opacity: 0.7 }}>⭐</span> : null}
                          </span>
                        )) : (
                          <span style={{ color: '#94a3b8', fontSize: 11 }}>No modules</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span style={u.is_active ? statusBadgeStyles.active : statusBadgeStyles.inactive}
                        onClick={() => toggleUserStatus(u)} title="Click to toggle">
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button className="um-action-btn edit" title="Edit User" onClick={() => openEditUser(u)}>✏️</button>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button className="um-action-btn password" title="Reset Password" onClick={() => { setPasswordModal(u); setNewPassword(''); }}>🔑</button>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr><td colSpan="9" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No users found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ROLES TAB */}
      {activeTab === 'roles' && (
        <>
          <div className="um-table-container">
            <table className="um-table">
              <thead>
                <tr>
                  <th>Role Name</th>
                  <th style={{ width: 150 }}>Users</th>
                  <th style={{ width: 180 }}>Permissions</th>
                  <th style={{ width: 100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.map(r => (
                  <tr key={r.id}>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: 20 }}>{roleIcons[r.role_code] || r.icon || '👤'}</span>
                        <div>
                          <div className="um-role-name">{r.role_name}</div>
                          {r.description && <div className="um-role-desc">{r.description}</div>}
                        </div>
                      </div>
                    </td>
                    <td><span style={countBadgeStyle}>👥 {r.user_count || 0} users</span></td>
                    <td><span style={countBadgeGreenStyle}>🔑 {r.permission_count || 0} permissions</span></td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'row', gap: '6px' }}>
                        <button className="um-action-btn edit" onClick={() => openEditRole(r)}>✏️</button>
                        {!r.is_system && r.role_code !== 'super_admin' && (
                          <button className="um-action-btn delete" onClick={() => setDeleteModal({ ...r, type: 'role' })}>🗑️</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ===== USER MODAL ===== */}
      {userModal && (
        <div style={S.overlay} onClick={() => setUserModal(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'white', display: 'flex', alignItems: 'center', gap: 8 }}>
                {userModal === 'add' ? '➕ Add New User' : '✏️ Edit User'}
              </h2>
              <button className="um-modal-close" onClick={() => setUserModal(null)}>×</button>
            </div>
            <div style={S.modalBody}>
              {/* Basic Information */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>
                  <span style={{ width: 28, height: 28, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, background: '#e3f2fd' }}>👤</span>
                  Basic Information
                </div>
                <div style={S.formRow}>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Full Name <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="text" placeholder="Enter full name" style={inputStyle}
                      value={userForm.full_name || ''} onChange={e => setUserForm(prev => ({ ...prev, full_name: e.target.value }))} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Email <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="email" placeholder="Enter email address" style={inputStyle}
                      value={userForm.email || ''} onChange={e => setUserForm(prev => ({ ...prev, email: e.target.value }))} />
                  </div>
                </div>
                <div style={S.formRow}>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Password {userModal === 'add' && <span style={{ color: '#ef4444' }}>*</span>}</label>
                    <input type="password" placeholder={userModal === 'edit' ? 'Leave blank to keep current' : 'Enter password'} style={inputStyle}
                      value={userForm.password || ''} onChange={e => setUserForm(prev => ({ ...prev, password: e.target.value }))} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Confirm Password</label>
                    <input type="password" placeholder="Confirm password" style={inputStyle}
                      value={userForm.confirm_password || ''} onChange={e => setUserForm(prev => ({ ...prev, confirm_password: e.target.value }))} />
                  </div>
                </div>
                <div style={S.formRow}>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Employee ID</label>
                    <input type="text" placeholder="e.g. EMP001" style={inputStyle}
                      value={userForm.employee_id || ''} onChange={e => setUserForm(prev => ({ ...prev, employee_id: e.target.value }))} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Phone</label>
                    <input type="text" placeholder="Enter phone number" style={inputStyle}
                      value={userForm.phone || ''} onChange={e => setUserForm(prev => ({ ...prev, phone: e.target.value }))} />
                  </div>
                </div>
                <div style={S.formRow}>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Designation</label>
                    <input type="text" placeholder="e.g. Production Manager" style={inputStyle}
                      value={userForm.designation || ''} onChange={e => setUserForm(prev => ({ ...prev, designation: e.target.value }))} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Department</label>
                    <input type="text" placeholder="e.g. Production" style={inputStyle}
                      value={userForm.department || ''} onChange={e => setUserForm(prev => ({ ...prev, department: e.target.value }))} />
                  </div>
                </div>
                <div style={S.formRow}>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Role <span style={{ color: '#ef4444' }}>*</span></label>
                    <select style={inputStyle} value={userForm.um_role_id || ''} onChange={e => handleRoleChange(e.target.value)}>
                      <option value="">Select Role</option>
                      {roles.map(r => <option key={r.id} value={r.id}>{r.role_name}</option>)}
                    </select>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, paddingTop: 28 }}>
                      <input type="checkbox" id="user-active"
                        style={{ width: 18, height: 18, accentColor: '#1565c0', margin: 0 }}
                        checked={!!userForm.is_active} onChange={e => setUserForm(prev => ({ ...prev, is_active: e.target.checked }))} />
                      <label htmlFor="user-active" style={{ ...labelStyle, margin: 0, marginBottom: 0 }}>Active</label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Module Access */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>
                  <span style={{ width: 28, height: 28, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, background: '#e3f2fd' }}>📦</span>
                  Application Module Access
                </div>
                {modules.length > 0 ? (
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                    {/* Header */}
                    <div style={{ display: 'grid', gridTemplateColumns: '50px 2fr 1fr 100px', padding: '10px 16px', background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)', color: 'white', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', alignItems: 'center' }}>
                      <div style={{ textAlign: 'center' }}>Access</div>
                      <div>Module</div>
                      <div>Description</div>
                      <div style={{ textAlign: 'center' }}>Default</div>
                    </div>
                    {/* Module Rows */}
                    {modules.map(m => {
                      const mid = parseInt(m.id);
                      const isChecked = userModules.includes(mid);
                      const isDefault = defaultModuleId === mid;
                      return (
                        <div key={m.id} style={{
                          display: 'grid', gridTemplateColumns: '50px 2fr 1fr 100px',
                          padding: '12px 16px', borderBottom: '1px solid #f1f5f9',
                          alignItems: 'center', fontSize: 13,
                          background: isChecked ? 'white' : '#fafafa',
                          opacity: isChecked ? 1 : 0.6,
                          transition: 'all 0.2s'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <input type="checkbox" checked={isChecked}
                              style={{ width: 18, height: 18, accentColor: m.color || '#1565c0', cursor: 'pointer', margin: 0 }}
                              onChange={e => {
                                if (e.target.checked) {
                                  const newMods = [...userModules, mid];
                                  setUserModules(newMods);
                                  if (newMods.length === 1) setDefaultModuleId(mid);
                                } else {
                                  const newMods = userModules.filter(id => id !== mid);
                                  setUserModules(newMods);
                                  if (defaultModuleId === mid) setDefaultModuleId(newMods.length > 0 ? newMods[0] : null);
                                }
                              }}
                            />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{
                              width: 34, height: 34, borderRadius: 8, display: 'inline-flex',
                              alignItems: 'center', justifyContent: 'center', fontSize: 18,
                              background: m.color || '#1565c0', color: 'white', flexShrink: 0
                            }}>
                              {m.icon || '📦'}
                            </span>
                            <div>
                              <div style={{ fontWeight: 600, color: '#1e293b' }}>{m.module_name}</div>
                              <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{m.module_code}</div>
                            </div>
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>{m.description || '—'}</div>
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <input type="radio" name="default_module" checked={isDefault} disabled={!isChecked}
                              style={{ width: 18, height: 18, accentColor: '#22c55e', cursor: isChecked ? 'pointer' : 'not-allowed', margin: 0 }}
                              onChange={() => { if (isChecked) setDefaultModuleId(mid); }}
                            />
                          </div>
                        </div>
                      );
                    })}
                    {/* Quick Actions */}
                    <div style={{ display: 'flex', gap: 8, padding: '10px 16px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                      <button type="button" className="um-quick-btn select-all" onClick={() => { const allIds = modules.map(m => parseInt(m.id)); setUserModules(allIds); if (!defaultModuleId && allIds.length > 0) setDefaultModuleId(allIds[0]); }}>✓ Select All</button>
                      <button type="button" className="um-quick-btn deselect-all" onClick={() => { setUserModules([]); setDefaultModuleId(null); }}>✕ Deselect All</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: 10, border: '1px dashed #e2e8f0' }}>
                    No modules available. Please create modules in Module Management first.
                  </div>
                )}
              </div>

              {/* Screen Permissions */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>
                  <span style={{ width: 28, height: 28, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, background: '#ffedd5' }}>🔐</span>
                  Screen Permissions
                </div>
                {/* Module Tabs */}
                {(() => {
                  const moduleCodes = [...new Set((screens || []).map(s => s.module_code || 'COLDSTORE'))];
                  const moduleLabels = { 'COLDSTORE': { name: 'Cold Store', icon: '🏭', color: '#1565c0' }, 'PPC': { name: 'PPC', icon: '👥', color: '#059669' }, 'EXECUTIVE': { name: 'Executive', icon: '📊', color: '#7c3aed' } };
                  return (
                    <>
                      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid #e2e8f0' }}>
                        {moduleCodes.map(code => {
                          const label = moduleLabels[code] || { name: code, icon: '📦', color: '#64748b' };
                          const isActive = permTab === code;
                          const screenCount = screens.filter(s => (s.module_code || 'COLDSTORE') === code).length;
                          return (
                            <button key={code} type="button"
                              onClick={() => setPermTab(code)}
                              style={{
                                padding: '10px 20px', border: 'none', background: 'none',
                                fontSize: 13, fontWeight: isActive ? 700 : 500,
                                color: isActive ? label.color : '#64748b',
                                borderBottom: isActive ? `3px solid ${label.color}` : '3px solid transparent',
                                marginBottom: '-2px', cursor: 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                transition: 'all 0.2s', fontFamily: 'inherit'
                              }}>
                              <span>{label.icon}</span> {label.name}
                              <span style={{
                                background: isActive ? label.color + '18' : '#e2e8f0',
                                color: isActive ? label.color : '#64748b',
                                padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600
                              }}>{screenCount}</span>
                            </button>
                          );
                        })}
                      </div>
                      <PermissionsTable
                        screens={screens.filter(s => (s.module_code || 'COLDSTORE') === permTab)}
                        permissions={userPermissions}
                        onChange={setUserPermissions}
                        showExpandCollapse={true}
                      />
                    </>
                  );
                })()}
              </div>
            </div>
            <div style={S.modalFooter}>
              <button className="um-btn um-btn-secondary" onClick={() => setUserModal(null)}>Cancel</button>
              <button className="um-btn um-btn-success" onClick={saveUser} disabled={saving}>
                {saving ? '⏳ Saving...' : '💾 Save User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== ROLE MODAL ===== */}
      {roleModal && (
        <div style={S.overlay} onClick={() => setRoleModal(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'white', display: 'flex', alignItems: 'center', gap: 8 }}>
                {roleModal === 'add' ? '➕ Add New Role' : `✏️ Edit Role: ${roleForm.role_name}`}
              </h2>
              <button className="um-modal-close" onClick={() => setRoleModal(null)}>×</button>
            </div>
            <div style={S.modalBody}>
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>
                  <span style={{ width: 28, height: 28, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, background: '#dcfce7' }}>🔐</span>
                  Role Information
                </div>
                <div style={S.formRow}>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Role Name <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="text" placeholder="Enter role name" style={inputStyle}
                      value={roleForm.role_name || ''} onChange={e => setRoleForm({ ...roleForm, role_name: e.target.value })} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Description</label>
                    <input type="text" placeholder="Enter description" style={inputStyle}
                      value={roleForm.description || ''} onChange={e => setRoleForm({ ...roleForm, description: e.target.value })} />
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>
                  <span style={{ width: 28, height: 28, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, background: '#ffedd5' }}>🔑</span>
                  Module Permissions
                </div>
                {/* Module Tabs */}
                {(() => {
                  const moduleCodes = [...new Set((screens || []).map(s => s.module_code || 'COLDSTORE'))];
                  const moduleLabels = { 'COLDSTORE': { name: 'Cold Store', icon: '🏭', color: '#1565c0' }, 'PPC': { name: 'PPC', icon: '👥', color: '#059669' }, 'EXECUTIVE': { name: 'Executive', icon: '📊', color: '#7c3aed' } };
                  return (
                    <>
                      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid #e2e8f0' }}>
                        {moduleCodes.map(code => {
                          const label = moduleLabels[code] || { name: code, icon: '📦', color: '#64748b' };
                          const isActive = permTab === code;
                          const screenCount = screens.filter(s => (s.module_code || 'COLDSTORE') === code).length;
                          return (
                            <button key={code} type="button"
                              onClick={() => setPermTab(code)}
                              style={{
                                padding: '10px 20px', border: 'none', background: 'none',
                                fontSize: 13, fontWeight: isActive ? 700 : 500,
                                color: isActive ? label.color : '#64748b',
                                borderBottom: isActive ? `3px solid ${label.color}` : '3px solid transparent',
                                marginBottom: '-2px', cursor: 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                transition: 'all 0.2s', fontFamily: 'inherit'
                              }}>
                              <span>{label.icon}</span> {label.name}
                              <span style={{
                                background: isActive ? label.color + '18' : '#e2e8f0',
                                color: isActive ? label.color : '#64748b',
                                padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600
                              }}>{screenCount}</span>
                            </button>
                          );
                        })}
                      </div>
                      <PermissionsTable
                        screens={screens.filter(s => (s.module_code || 'COLDSTORE') === permTab)}
                        permissions={rolePermissions}
                        onChange={setRolePermissions}
                        showExpandCollapse={false}
                      />
                    </>
                  );
                })()}
              </div>
            </div>
            <div style={S.modalFooter}>
              <button className="um-btn um-btn-secondary" onClick={() => setRoleModal(null)}>Cancel</button>
              <button className="um-btn um-btn-success" onClick={saveRole} disabled={saving}>
                {saving ? '⏳ Saving...' : '💾 Save Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== DELETE CONFIRM MODAL ===== */}
      {deleteModal && (
        <div style={S.overlay} onClick={() => setDeleteModal(null)}>
          <div style={S.modalSm} onClick={e => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'white' }}>⚠️ Confirm Delete</h2>
              <button className="um-modal-close" onClick={() => setDeleteModal(null)}>×</button>
            </div>
            <div style={S.modalBody}>
              <div style={{ textAlign: 'center', padding: 20 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🗑️</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>
                  Delete {deleteModal.type === 'user' ? 'user' : 'role'} "{deleteModal.full_name || deleteModal.role_name}"?
                </div>
                <div style={{ fontSize: 14, color: '#94a3b8' }}>This action cannot be undone.</div>
              </div>
            </div>
            <div style={S.modalFooter}>
              <button className="um-btn um-btn-secondary" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="um-btn um-btn-danger" onClick={deleteModal.type === 'user' ? deleteUser : deleteRole}>🗑️ Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== PASSWORD RESET MODAL ===== */}
      {passwordModal && (
        <div style={S.overlay} onClick={() => setPasswordModal(null)}>
          <div style={S.modalSm} onClick={e => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'white' }}>🔑 Reset Password</h2>
              <button className="um-modal-close" onClick={() => setPasswordModal(null)}>×</button>
            </div>
            <div style={S.modalBody}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>New Password for <strong>{passwordModal.full_name || passwordModal.username}</strong></label>
                <input type="password" placeholder="Enter new password" style={inputStyle}
                  value={newPassword} onChange={e => setNewPassword(e.target.value)} />
              </div>
            </div>
            <div style={S.modalFooter}>
              <button className="um-btn um-btn-secondary" onClick={() => setPasswordModal(null)}>Cancel</button>
              <button className="um-btn um-btn-primary" onClick={resetPassword}>🔑 Reset Password</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;